import { Dimensions, Platform, StyleSheet } from 'react-native';
import FontFamily from '../../../../constants/fonts';

const COLORS = {
  bgPage: '#F5F9FF',
  bgSurface: '#FFFFFF',
  blue900: '#01346F',
  blue800: '#034895',
  blue25: '#F3F7FD',
  textPrimary: '#2D2D2D',
  textSecondary: '#616161',
  textMuted: '#838383',
  borderDefault: '#D0D0D0',
  borderTableHeader: '#D7E5F8',
  gray25: '#EBEBEB',
  gray50: '#E6E6E6',
};

const SPACING = { xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24 };
const RADIUS = { sm: 4, md: 6, lg: 8, xl: 14 };

const { width } = Dimensions.get('window');

export const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: 'transparent' },
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(45, 45, 45, 0.45)', zIndex: 0 },
  drawerPanel: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: Math.min(420, width * 0.92),
    backgroundColor: COLORS.bgSurface,
    zIndex: 55,
    flexDirection: 'column',
    ...Platform.select({
      ios: { shadowColor: '#000', shadowOffset: { width: -6, height: 0 }, shadowOpacity: 0.16, shadowRadius: 24 },
      android: { elevation: 8 },
    }),
  },

  modalBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(18, 35, 55, 0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: SPACING.lg,
  },
  modalContainer: {
    width: '100%',
    maxWidth: 780,
    maxHeight: '92%',
    borderWidth: 8,
    borderColor: '#D8E7FB',
    borderRadius: 12,
    backgroundColor: COLORS.bgPage,
    overflow: 'hidden',
    flexDirection: 'column',
    ...Platform.select({
      ios: { shadowColor: '#005EBF', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 24 },
      android: { elevation: 12 },
    }),
  },

  modalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 14,
    paddingVertical: SPACING.lg,
    paddingHorizontal: 18,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderDefault,
    backgroundColor: COLORS.bgPage,
  },
  modalHeaderContent: { flex: 1 },
  modalTitle: {
    fontSize: 19,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: COLORS.textPrimary,
    marginBottom: 3,
  },
  modalSubtitle: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: COLORS.textMuted,
    lineHeight: 16,
    marginBottom: 0,
  },
  modalHeaderActionsRow: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#BFD5F3',
    backgroundColor: '#EFF5FD',
    borderRadius: 999,
    paddingHorizontal: 10,
    height: 28,
  },
  headerActionButtonDisabled: {
    borderColor: COLORS.borderDefault,
    backgroundColor: '#F3F3F3',
  },
  headerActionButtonText: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: COLORS.blue800,
  },
  headerActionButtonTextDisabled: {
    color: COLORS.textMuted,
  },
  modalHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  syncTagModal: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 24,
    paddingHorizontal: 9,
    borderRadius: 12,
    backgroundColor: '#DDEBFB',
  },
  syncTagModalText: {
    fontSize: 10,
    fontWeight: '800',
    fontFamily: FontFamily.bold,
    color: COLORS.blue900,
  },
  modalCloseBtn: {
    padding: SPACING.xs,
  },
  modalCloseBtnText: {
    fontSize: 21,
    fontWeight: '400',
    fontFamily: FontFamily.regular,
    color: COLORS.textMuted,
    lineHeight: 24,
  },

  modalTabsContainer: {
    paddingHorizontal: 18,
    paddingTop: SPACING.md,
    paddingBottom: SPACING.md,
    backgroundColor: COLORS.bgPage,
  },
  modalTabs: {
    flexDirection: 'row',
    gap: SPACING.xs,
    padding: SPACING.xs,
    backgroundColor: '#E7EDF5',
    borderRadius: RADIUS.lg,
  },
  modalTab: {
    flex: 1,
    minHeight: 38,
    borderRadius: RADIUS.md,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: SPACING.xs,
  },
  modalTabActive: {
    backgroundColor: COLORS.bgSurface,
    ...Platform.select({
      ios: { shadowColor: '#12233712', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.12, shadowRadius: 4 },
      android: { elevation: 2 },
    }),
  },
  modalTabText: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: COLORS.textSecondary,
    textAlign: 'center',
  },
  modalTabTextActive: {
    fontFamily: FontFamily.bold,
    color: COLORS.blue900,
  },

  modalBody: { flex: 1 },
  modalBodyContent: {
    paddingVertical: 17,
    paddingHorizontal: 19,
    paddingBottom: SPACING.xl,
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: SPACING.sm,
    paddingVertical: 13,
    paddingHorizontal: 18,
    borderTopWidth: 1,
    borderTopColor: COLORS.borderDefault,
    backgroundColor: COLORS.bgPage,
  },
  footerBtn: {
    paddingVertical: SPACING.xs,
    marginRight: 'auto',
  },
  footerBtnText: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: COLORS.blue800,
  },
  footerBtnSecondary: {
    height: 34,
    paddingHorizontal: 15,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: COLORS.blue800,
    backgroundColor: COLORS.bgSurface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerBtnSecondaryText: {
    fontSize: 13,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: COLORS.blue800,
  },
  footerBtnPrimary: {
    height: 34,
    paddingHorizontal: 15,
    borderRadius: 24,
    backgroundColor: COLORS.blue800,
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerBtnPrimaryText: {
    fontSize: 13,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#fff',
  },

  ppSec: { marginBottom: 14 },
  ppSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderDefault,
    paddingBottom: 7,
    marginBottom: 0,
  },
  ppSt: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: COLORS.blue900,
  },
  ppChevron: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: COLORS.textMuted,
  },
  ppGrid: {
    paddingTop: 14,
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 18,
    rowGap: 12,
  },
  ppCell: { flex: 1, minWidth: 140 },
  ppLabelRow: { flexDirection: 'row', alignItems: 'center', gap: SPACING.xs, marginBottom: 3 },
  ppLabel: {
    fontSize: 10,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    textTransform: 'uppercase',
    color: COLORS.textMuted,
    letterSpacing: 0.25,
  },
  ppValue: {
    fontSize: 12,
    color: COLORS.textPrimary,
    fontWeight: '600',
    fontFamily: FontFamily.medium,
  },
  ppValueEmpty: {
    color: COLORS.textMuted,
    fontWeight: '400',
    fontFamily: FontFamily.regular,
  },

  collapseSectionBar: {
    marginBottom: SPACING.md,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  collapseAllText: {
    fontSize: 11,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: COLORS.blue800,
  },

  edfLockBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 'auto',
    gap: 3,
    backgroundColor: 'transparent',
  },
  edfLockText: {
    fontSize: 9,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: COLORS.blue800,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },

  detailSection: {
    backgroundColor: COLORS.bgSurface,
    borderRadius: RADIUS.lg,
    marginBottom: 14,
    padding: SPACING.lg,
    ...Platform.select({
      ios: { shadowColor: '#005EC0', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.08, shadowRadius: 24 },
      android: { elevation: 2 },
    }),
  },
  subtableHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: SPACING.md,
  },
  subtableTitle: {
    fontSize: 14,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: COLORS.textPrimary,
    marginBottom: 2,
  },
  subtableSubtitle: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: COLORS.textMuted,
  },
  tableWrap: {
    borderWidth: 1,
    borderColor: COLORS.borderDefault,
    borderRadius: 7,
    overflow: 'hidden',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: COLORS.blue25,
    paddingVertical: 9,
    paddingHorizontal: 9,
    borderBottomWidth: 2,
    borderBottomColor: COLORS.borderTableHeader,
  },
  tableHeaderCell: {
    fontSize: 11,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: COLORS.textSecondary,
    letterSpacing: 0.3,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 9,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.borderDefault,
  },
  tableRowLast: { borderBottomWidth: 0 },
  tableCell: {
    fontSize: 11,
    color: COLORS.textPrimary,
    fontFamily: FontFamily.regular,
  },
  tableCellBold: {
    fontWeight: '600',
    fontFamily: FontFamily.medium,
  },
  colName: { flex: 2 },
  colId: { flex: 1.5 },
  colAccess: { flex: 1.5 },
  colLogin: { flex: 1.5 },
  colRoute: { flex: 1.5 },
  colType: { flex: 2 },
  colActive: { flex: 1 },

  accessBadge: {
    height: 20,
    paddingHorizontal: SPACING.sm,
    borderRadius: RADIUS.sm,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  accessBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#fff',
  },
  routeTypeBadge: {
    height: 20,
    paddingHorizontal: SPACING.sm,
    borderRadius: RADIUS.sm,
    backgroundColor: COLORS.blue800,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  routeTypeText: {
    fontSize: 10,
    fontWeight: '600',
    fontFamily: FontFamily.medium,
    color: COLORS.blue800,
  },
  activeIndicator: {
    height: 20,
    paddingHorizontal: SPACING.sm,
    borderRadius: RADIUS.sm,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  activeOn: { backgroundColor: '#DDF2E1' },
  activeOff: { backgroundColor: COLORS.gray50 },
  activeIndicatorText: {
    fontSize: 10,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },
  activeOnText: { color: '#176B2C' },
  activeOffText: { color: COLORS.textSecondary },

  duoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    marginBottom: 13,
    marginTop: 10,
  },
  duoLabel: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: COLORS.textPrimary,
  },
  duoStatus: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: COLORS.textSecondary,
  },

  emptyState: {
    paddingVertical: SPACING.xl,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: COLORS.textMuted,
    textAlign: 'center',
  },

  btnSecondary: {
    height: 32,
    paddingHorizontal: SPACING.md,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: COLORS.blue800,
    backgroundColor: COLORS.bgSurface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnSecondaryText: {
    fontSize: 13,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: COLORS.blue800,
  },
  btnPrimary: {
    height: 32,
    paddingHorizontal: SPACING.md,
    borderRadius: 24,
    backgroundColor: COLORS.blue800,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnPrimaryText: {
    fontSize: 13,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#fff',
  },
});
